import { Component } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '@config/firebase';

/**
 * ErrorBoundary global. Si cualquier hijo crashea, evita la pantalla en blanco
 * y muestra una UI amable con botón de recarga.
 *
 * También registra el error en /errorLogs (Firestore) si el usuario está
 * autenticado, para que el super_admin pueda revisar fallos en producción.
 *
 * Los class components siguen siendo la única forma de crear un error
 * boundary en React (los hooks no pueden capturar errores de render todavía).
 */
export class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    // Error de "chunk obsoleto" (desplegamos una versión nueva mientras el
    // usuario tenía la app abierta). Recargamos para coger la versión nueva,
    // con un contador (máx 2) que evita el bucle si el chunk falla siempre.
    const msg = String(error?.message || '');
    if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) {
      try {
        const count = parseInt(sessionStorage.getItem('chunkReloadCount') || '0', 10);
        if (count < 2) {
          sessionStorage.setItem('chunkReloadCount', String(count + 1));
          window.location.reload();
          return { hasError: false, error: null };
        }
      } catch {}
    }
    return { hasError: true, error };
  }

  async componentDidCatch(error, info) {
    console.error('ErrorBoundary capturó:', error, info);
    // Intentamos registrar el error en Firestore (best-effort, no bloquea UI)
    if (auth.currentUser) {
      try {
        await addDoc(collection(db, 'errorLogs'), {
          message: String(error?.message || error || 'unknown').slice(0, 2000),
          stack: String(error?.stack || '').slice(0, 5000),
          componentStack: String(info?.componentStack || '').slice(0, 2000),
          url: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
          email: auth.currentUser?.email || '',
          uid: auth.currentUser?.uid || '',
          createdAt: serverTimestamp(),
        });
      } catch (e) {
        // El log falló también — al menos lo tenemos en la consola del navegador
        console.warn('No se pudo registrar el error en Firestore:', e);
      }
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    try { window.location.reload(); } catch {}
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
          background: 'var(--bg-primary, #0a1228)',
          color: 'var(--text-primary, #e2e8f0)',
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: '100%',
            padding: 32,
            borderRadius: 16,
            background: 'rgba(22, 33, 62, 0.6)',
            border: '1px solid rgba(255, 215, 0, 0.25)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            textAlign: 'center',
          }}
        >
          <i
            className="fa-solid fa-triangle-exclamation"
            style={{ fontSize: '3rem', color: 'var(--color-yellow, #FFD700)', marginBottom: 16 }}
          />
          <h1 style={{ margin: '0 0 12px 0', fontSize: '1.5rem' }}>Algo se ha roto</h1>
          <p style={{ margin: '0 0 24px 0', opacity: 0.75 }}>
            Ha ocurrido un error inesperado. El equipo de TorneosPro ya tiene
            el aviso. Puedes recargar la página para seguir trabajando.
          </p>
          {this.state.error && (
            <details style={{
              textAlign: 'left',
              background: 'rgba(0,0,0,0.25)',
              padding: 12,
              borderRadius: 8,
              marginBottom: 24,
              fontSize: '0.8rem',
            }}>
              <summary style={{ cursor: 'pointer', opacity: 0.7 }}>Detalles técnicos</summary>
              <pre style={{
                margin: '8px 0 0 0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}>{String(this.state.error?.message || this.state.error)}</pre>
            </details>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.reload}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                border: 'none',
                background: 'var(--color-yellow, #FFD700)',
                color: '#0a1228',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <i className="fa-solid fa-arrow-rotate-right" style={{ marginRight: 6 }}></i>
              Recargar
            </button>
            <button
              type="button"
              onClick={this.reset}
              style={{
                padding: '10px 18px',
                borderRadius: 8,
                background: 'transparent',
                color: 'inherit',
                border: '1px solid rgba(255,255,255,0.2)',
                cursor: 'pointer',
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
